/**
 * Performance-Optimized Animation System
 * Advanced animation pooling, batching, and reduced motion support for enterprise-grade performance
 */

import {
  AnimationPool,
  AnimationBatch,
  AnimationRequest,
  ReducedMotionConfig,
  AnimationPerformanceMetrics,
  GPUAnimationProperties,
  AnimationTimingConfig,
} from "./types";
import { HardwareAcceleratedAnimationFramework } from "./HardwareAcceleratedAnimationFramework";

/**
 * Performance-Optimized Animation System
 * Orchestrates all animation performance optimizations
 */
export class PerformanceOptimizedAnimationSystem {
  private animationFramework: HardwareAcceleratedAnimationFramework;
  private poolManager: AnimationPoolManager;
  private batchProcessor: AnimationBatchProcessor;
  private performanceMonitor: AnimationPerformanceMonitor;
  private reducedMotionManager: ReducedMotionManager;
  private memoryManager: AnimationMemoryManager;
  private adaptiveQualitySystem: AdaptiveQualitySystem;

  constructor(
    animationFramework?: HardwareAcceleratedAnimationFramework,
    performanceConfig?: Partial<PerformanceConfig>,
  ) {
    this.animationFramework =
      animationFramework || new HardwareAcceleratedAnimationFramework();

    const config = {
      enablePooling: true,
      enableBatching: true,
      enableAdaptiveQuality: true,
      enableMemoryManagement: true,
      maxConcurrentAnimations: 50,
      targetFrameRate: 60,
      memoryThreshold: 100 * 1024 * 1024, // 100MB
      ...performanceConfig,
    };

    this.initializePerformanceSystems(config);
  }

  /**
   * Initialize all performance optimization systems
   */
  private initializePerformanceSystems(config: PerformanceConfig): void {
    this.poolManager = new AnimationPoolManager(config);
    this.batchProcessor = new AnimationBatchProcessor(
      this.animationFramework,
      config,
    );
    this.performanceMonitor = new AnimationPerformanceMonitor(config);
    this.reducedMotionManager = new ReducedMotionManager();
    this.memoryManager = new AnimationMemoryManager(config);
    this.adaptiveQualitySystem = new AdaptiveQualitySystem(config);

    // Setup monitoring and adaptive behaviors
    this.setupPerformanceMonitoring();
    this.setupAdaptiveQuality();
  }

  /**
   * Setup performance monitoring and callbacks
   */
  private setupPerformanceMonitoring(): void {
    this.performanceMonitor.onPerformanceIssue = (metrics) => {
      console.warn("Animation performance issue detected:", metrics);
      this.handlePerformanceIssue(metrics);
    };

    this.performanceMonitor.onMemoryThresholdExceeded = (usage) => {
      this.memoryManager.triggerCleanup();
    };

    this.performanceMonitor.startMonitoring();
  }

  /**
   * Setup adaptive quality system
   */
  private setupAdaptiveQuality(): void {
    this.adaptiveQualitySystem.onQualityAdjustment = (newLevel) => {
      console.log("Animation quality adjusted to:", newLevel);
      this.applyQualityLevel(newLevel);
    };
  }

  /**
   * Create optimized animation with performance considerations
   */
  public async createOptimizedAnimation(
    element: HTMLElement,
    properties: GPUAnimationProperties,
    timing: AnimationTimingConfig,
    options: OptimizedAnimationOptions = {},
  ): Promise<void> {
    // Check for reduced motion preference
    if (this.reducedMotionManager.shouldUseReducedMotion()) {
      return this.createReducedMotionAnimation(element, properties, timing);
    }

    // Check if we should use pooled animation
    if (
      options.usePool !== false &&
      this.poolManager.shouldUsePool(options.animationType)
    ) {
      return this.createPooledAnimation(element, properties, timing, options);
    }

    // Check if we should batch this animation
    if (
      options.batchable !== false &&
      this.batchProcessor.shouldBatch(options)
    ) {
      return this.batchProcessor.addToBatch({
        elementId: element.id || `elem-${Date.now()}`,
        animationType: options.animationType || "default",
        properties,
        timing,
        callback: options.callback,
      });
    }

    // Create regular optimized animation
    return this.createRegularOptimizedAnimation(
      element,
      properties,
      timing,
      options,
    );
  }

  /**
   * Create pooled animation for memory efficiency
   */
  private async createPooledAnimation(
    element: HTMLElement,
    properties: GPUAnimationProperties,
    timing: AnimationTimingConfig,
    options: OptimizedAnimationOptions,
  ): Promise<void> {
    const animationType = options.animationType || "default";
    const pooledAnimation = this.poolManager.getFromPool(animationType);

    if (pooledAnimation) {
      // Reuse existing animation
      return this.reusePooledAnimation(
        pooledAnimation,
        element,
        properties,
        timing,
      );
    } else {
      // Create new animation and add to pool when complete
      return this.createAndPoolAnimation(element, properties, timing, options);
    }
  }

  /**
   * Create reduced motion animation
   */
  private async createReducedMotionAnimation(
    element: HTMLElement,
    properties: GPUAnimationProperties,
    timing: AnimationTimingConfig,
  ): Promise<void> {
    const reducedTiming =
      this.reducedMotionManager.getReducedMotionTiming(timing);
    const reducedProperties =
      this.reducedMotionManager.getReducedMotionProperties(properties);

    return this.animationFramework.createAnimation(
      element,
      reducedProperties,
      reducedTiming,
    );
  }

  /**
   * Create regular optimized animation with performance monitoring
   */
  private async createRegularOptimizedAnimation(
    element: HTMLElement,
    properties: GPUAnimationProperties,
    timing: AnimationTimingConfig,
    options: OptimizedAnimationOptions,
  ): Promise<void> {
    // Apply current quality level adjustments
    const adjustedProperties =
      this.adaptiveQualitySystem.adjustProperties(properties);
    const adjustedTiming = this.adaptiveQualitySystem.adjustTiming(timing);

    // Start performance monitoring for this animation
    const animationId = options.id || `anim-${Date.now()}`;
    this.performanceMonitor.startAnimationTracking(animationId);

    try {
      await this.animationFramework.createAnimation(
        element,
        adjustedProperties,
        adjustedTiming,
        { id: animationId, priority: options.priority },
      );

      // Record successful completion
      this.performanceMonitor.recordAnimationSuccess(animationId);
    } catch (error) {
      // Record failure
      this.performanceMonitor.recordAnimationFailure(animationId, error);
      throw error;
    } finally {
      this.performanceMonitor.stopAnimationTracking(animationId);
    }
  }

  /**
   * Reuse pooled animation
   */
  private async reusePooledAnimation(
    pooledAnimation: PooledAnimation,
    element: HTMLElement,
    properties: GPUAnimationProperties,
    timing: AnimationTimingConfig,
  ): Promise<void> {
    // Reset animation to new parameters
    pooledAnimation.reset();
    pooledAnimation.configure(element, properties, timing);

    return pooledAnimation.play();
  }

  /**
   * Create animation and add to pool when complete
   */
  private async createAndPoolAnimation(
    element: HTMLElement,
    properties: GPUAnimationProperties,
    timing: AnimationTimingConfig,
    options: OptimizedAnimationOptions,
  ): Promise<void> {
    const animationType = options.animationType || "default";

    await this.animationFramework.createAnimation(element, properties, timing);

    // Create pooled version for future reuse
    if (this.poolManager.shouldPool(animationType)) {
      const pooledAnimation = new PooledAnimation(element, properties, timing);
      this.poolManager.addToPool(animationType, pooledAnimation);
    }
  }

  /**
   * Handle performance issues by adjusting system behavior
   */
  public handlePerformanceIssue(metrics: AnimationPerformanceMetrics): void {
    // Reduce animation quality if frame drops detected
    if (metrics.frameDropCount > 10) {
      this.adaptiveQualitySystem.reduceQuality();
    }

    // Enable more aggressive batching if many concurrent animations
    if (metrics.animationLatency > 50) {
      this.batchProcessor.enableAggressiveBatching();
    }

    // Trigger memory cleanup if usage is high
    if (metrics.memoryUsage > 80 * 1024 * 1024) {
      // 80MB
      this.memoryManager.triggerCleanup();
    }

    // Consider enabling reduced motion if performance is severely impacted
    if (metrics.frameDropCount > 20 && metrics.frameRate < 30) {
      this.reducedMotionManager.suggestReducedMotion();
    }
  }

  /**
   * Apply quality level adjustments
   */
  private applyQualityLevel(level: "low" | "medium" | "high"): void {
    switch (level) {
      case "low":
        this.batchProcessor.enableAggressiveBatching();
        this.poolManager.increasePoolingThreshold();
        break;
      case "medium":
        this.batchProcessor.enableModerateBatching();
        this.poolManager.setDefaultPoolingThreshold();
        break;
      case "high":
        this.batchProcessor.disableAggressiveBatching();
        this.poolManager.reducePoolingThreshold();
        break;
    }
  }

  /**
   * Preload animation pool for common animations
   */
  public preloadAnimationPools(animationTypes: string[]): Promise<void[]> {
    return Promise.all(
      animationTypes.map((type) => this.poolManager.preloadPool(type)),
    );
  }

  /**
   * Get performance metrics
   */
  public getPerformanceMetrics(): AnimationPerformanceMetrics {
    return this.performanceMonitor.getMetrics();
  }

  /**
   * Force cleanup of resources
   */
  public forceCleanup(): void {
    this.memoryManager.triggerCleanup();
    this.poolManager.cleanup();
    this.batchProcessor.processPendingBatches();
  }

  /**
   * Optimize for spatial context (viewport culling, LOD, etc.)
   */
  public async optimizeForSpatialContext(
    context: any,
    element?: HTMLElement,
  ): Promise<void> {
    // Implementation for spatial optimization would go here
    // This could include viewport culling, level-of-detail adjustments, etc.
    if (element && context.isVisible === false) {
      // Element is outside viewport, reduce animation quality
      this.adaptiveQualitySystem.reduceQuality();
    }
  }

  /**
   * Dispose of all resources
   */
  public dispose(): void {
    this.performanceMonitor.stopMonitoring();
    this.poolManager.dispose();
    this.batchProcessor.dispose();
    this.memoryManager.dispose();
    this.adaptiveQualitySystem.dispose();
  }
}

/**
 * Animation Pool Manager
 * Manages pools of reusable animations for memory efficiency
 */
class AnimationPoolManager {
  private pools: Map<string, AnimationPool> = new Map();
  private config: PerformanceConfig;

  constructor(config: PerformanceConfig) {
    this.config = config;
    this.initializePools();
  }

  private initializePools(): void {
    const commonAnimationTypes = [
      "fade",
      "slide",
      "scale",
      "rotate",
      "bounce",
      "shake",
      "pulse",
      "glow",
      "flip",
      "zoom",
    ];

    commonAnimationTypes.forEach((type) => {
      this.pools.set(type, {
        id: type,
        animations: new Map(),
        maxPoolSize: 20,
        recycledAnimations: [],
        activeAnimations: new Set(),
      });
    });
  }

  shouldUsePool(animationType?: string): boolean {
    return (
      this.config.enablePooling &&
      !!animationType &&
      this.pools.has(animationType)
    );
  }

  shouldPool(animationType: string): boolean {
    const pool = this.pools.get(animationType);
    return !!pool && pool.recycledAnimations.length < pool.maxPoolSize;
  }

  getFromPool(animationType: string): PooledAnimation | null {
    const pool = this.pools.get(animationType);
    if (!pool || pool.recycledAnimations.length === 0) {
      return null;
    }

    return pool.recycledAnimations.pop() || null;
  }

  addToPool(animationType: string, animation: PooledAnimation): void {
    const pool = this.pools.get(animationType);
    if (!pool || pool.recycledAnimations.length >= pool.maxPoolSize) {
      animation.dispose();
      return;
    }

    pool.recycledAnimations.push(animation);
  }

  async preloadPool(animationType: string, count: number = 5): Promise<void> {
    const pool = this.pools.get(animationType);
    if (!pool) return;

    // Create dummy animations to populate pool
    const promises = Array(count)
      .fill(null)
      .map(async () => {
        // This would create a template animation for the pool
        const templateElement = document.createElement("div");
        const pooledAnimation = new PooledAnimation(
          templateElement,
          this.getTemplateProperties(animationType),
          this.getTemplateTiming(animationType),
        );
        this.addToPool(animationType, pooledAnimation);
      });

    await Promise.all(promises);
  }

  increasePoolingThreshold(): void {
    this.pools.forEach((pool) => {
      pool.maxPoolSize = Math.min(pool.maxPoolSize * 1.5, 50);
    });
  }

  setDefaultPoolingThreshold(): void {
    this.pools.forEach((pool) => {
      pool.maxPoolSize = 20;
    });
  }

  reducePoolingThreshold(): void {
    this.pools.forEach((pool) => {
      pool.maxPoolSize = Math.max(pool.maxPoolSize * 0.7, 5);
    });
  }

  cleanup(): void {
    this.pools.forEach((pool) => {
      // Keep only half the recycled animations
      const keepCount = Math.floor(pool.recycledAnimations.length / 2);
      const toDispose = pool.recycledAnimations.splice(keepCount);
      toDispose.forEach((anim) => anim.dispose());
    });
  }

  private getTemplateProperties(animationType: string): GPUAnimationProperties {
    // Return default properties for each animation type
    const templates = {
      fade: {
        transform: "translateZ(0)",
        opacity: 0,
        willChange: ["opacity"],
        backfaceVisibility: "hidden" as const,
        perspective: 1000,
      },
      scale: {
        transform: "translateZ(0) scale(1.2)",
        opacity: 1,
        willChange: ["transform"],
        backfaceVisibility: "hidden" as const,
        perspective: 1000,
      },
      slide: {
        transform: "translateZ(0) translateX(100px)",
        opacity: 1,
        willChange: ["transform"],
        backfaceVisibility: "hidden" as const,
        perspective: 1000,
      },
    } as Record<string, GPUAnimationProperties>;

    return templates[animationType] || templates.fade;
  }

  private getTemplateTiming(animationType: string): AnimationTimingConfig {
    return {
      duration: 300,
      easing: "ease-out",
      delay: 0,
      iterations: 1,
      direction: "normal",
      fillMode: "forwards",
    };
  }

  dispose(): void {
    this.pools.forEach((pool) => {
      pool.recycledAnimations.forEach((anim) => anim.dispose());
      pool.activeAnimations.clear();
      pool.animations.clear();
    });
    this.pools.clear();
  }
}

/**
 * Animation Batch Processor
 * Groups animations for efficient processing
 */
class AnimationBatchProcessor {
  private animationFramework: HardwareAcceleratedAnimationFramework;
  private config: PerformanceConfig;
  private batchQueue: AnimationBatch[] = [];
  private batchTimer: number | null = null;
  private aggressiveBatching: boolean = false;

  constructor(
    animationFramework: HardwareAcceleratedAnimationFramework,
    config: PerformanceConfig,
  ) {
    this.animationFramework = animationFramework;
    this.config = config;
  }

  shouldBatch(options: OptimizedAnimationOptions): boolean {
    return this.config.enableBatching && options.batchable !== false;
  }

  addToBatch(request: AnimationRequest): Promise<void> {
    return new Promise((resolve, reject) => {
      // Find existing batch or create new one
      let batch = this.findSuitableBatch(request);

      if (!batch) {
        batch = this.createNewBatch(request);
        this.batchQueue.push(batch);
      }

      batch.animations.push({ ...request, resolve, reject } as any);

      // Schedule batch processing
      this.scheduleBatchProcessing();
    });
  }

  private findSuitableBatch(request: AnimationRequest): AnimationBatch | null {
    return (
      this.batchQueue.find(
        (batch) =>
          !batch.processed &&
          batch.animations.length < (this.aggressiveBatching ? 20 : 10) &&
          batch.priority === this.getPriority(request),
      ) || null
    );
  }

  private createNewBatch(request: AnimationRequest): AnimationBatch {
    return {
      id: `batch-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      animations: [],
      priority: this.getPriority(request),
      batchSize: 0,
      processed: false,
    };
  }

  private getPriority(request: AnimationRequest): "high" | "normal" | "low" {
    // Determine priority based on animation type
    const highPriorityTypes = ["user-interaction", "feedback", "error"];
    const lowPriorityTypes = ["decoration", "ambient", "background"];

    if (highPriorityTypes.includes(request.animationType)) return "high";
    if (lowPriorityTypes.includes(request.animationType)) return "low";
    return "normal";
  }

  private scheduleBatchProcessing(): void {
    if (this.batchTimer) return;

    const delay = this.aggressiveBatching ? 8 : 16; // Faster batching when aggressive

    this.batchTimer = window.setTimeout(() => {
      this.processPendingBatches();
      this.batchTimer = null;
    }, delay);
  }

  processPendingBatches(): Promise<void[]> {
    const batches = [...this.batchQueue.filter((b) => !b.processed)];
    this.batchQueue = this.batchQueue.filter((b) => b.processed);

    // Sort by priority
    batches.sort((a, b) => {
      const priorities = { high: 3, normal: 2, low: 1 };
      return priorities[b.priority] - priorities[a.priority];
    });

    return Promise.all(batches.map((batch) => this.processBatch(batch)));
  }

  private async processBatch(batch: AnimationBatch): Promise<void> {
    batch.processed = true;
    batch.batchSize = batch.animations.length;

    // Use requestAnimationFrame for optimal timing
    return new Promise((resolve) => {
      requestAnimationFrame(async () => {
        try {
          // Process animations in parallel for performance
          const promises = batch.animations.map(async (request: any) => {
            try {
              const element = document.getElementById(request.elementId);
              if (element) {
                await this.animationFramework.createAnimation(
                  element,
                  request.properties,
                  request.timing,
                );
                request.resolve();
              } else {
                request.reject(
                  new Error(`Element not found: ${request.elementId}`),
                );
              }
            } catch (error) {
              request.reject(error);
            }
          });

          await Promise.all(promises);
          resolve();
        } catch (error) {
          console.error("Batch processing error:", error);
          resolve();
        }
      });
    });
  }

  enableAggressiveBatching(): void {
    this.aggressiveBatching = true;
  }

  enableModerateBatching(): void {
    this.aggressiveBatching = false;
  }

  disableAggressiveBatching(): void {
    this.aggressiveBatching = false;
  }

  dispose(): void {
    if (this.batchTimer) {
      clearTimeout(this.batchTimer);
    }
    this.batchQueue = [];
  }
}

/**
 * Animation Performance Monitor
 * Tracks performance metrics and detects issues
 */
class AnimationPerformanceMonitor {
  private config: PerformanceConfig;
  private metrics: AnimationPerformanceMetrics;
  private activeAnimations: Map<string, number> = new Map();
  private monitoringInterval: number | null = null;

  public onPerformanceIssue?: (metrics: AnimationPerformanceMetrics) => void;
  public onMemoryThresholdExceeded?: (usage: number) => void;

  constructor(config: PerformanceConfig) {
    this.config = config;
    this.metrics = {
      frameRate: 60,
      frameDropCount: 0,
      animationLatency: 0,
      memoryUsage: 0,
      gpuLayersActive: 0,
      reducedMotionActive: false,
    };
  }

  startMonitoring(): void {
    this.monitoringInterval = window.setInterval(() => {
      this.updateMetrics();
      this.checkForIssues();
    }, 1000);
  }

  stopMonitoring(): void {
    if (this.monitoringInterval) {
      clearInterval(this.monitoringInterval);
      this.monitoringInterval = null;
    }
  }

  startAnimationTracking(animationId: string): void {
    this.activeAnimations.set(animationId, performance.now());
  }

  stopAnimationTracking(animationId: string): void {
    const startTime = this.activeAnimations.get(animationId);
    if (startTime) {
      const duration = performance.now() - startTime;
      this.metrics.animationLatency =
        (this.metrics.animationLatency + duration) / 2; // Moving average
      this.activeAnimations.delete(animationId);
    }
  }

  recordAnimationSuccess(animationId: string): void {
    // Record successful animation completion
  }

  recordAnimationFailure(animationId: string, error: any): void {
    console.error(`Animation ${animationId} failed:`, error);
  }

  private updateMetrics(): void {
    // Update frame rate
    this.updateFrameRate();

    // Update memory usage
    this.updateMemoryUsage();

    // Update GPU layers count
    this.updateGPULayersCount();
  }

  private updateFrameRate(): void {
    // Use performance timeline API to measure frame rate
    if ("performance" in window && "measure" in performance) {
      // Implementation would measure actual frame rate
      // For now, using a simplified approach
      this.metrics.frameRate = 60; // Default assumption
    }
  }

  private updateMemoryUsage(): void {
    if ("memory" in performance) {
      this.metrics.memoryUsage = (performance as any).memory.usedJSHeapSize;
    }
  }

  private updateGPULayersCount(): void {
    // Count elements with will-change or transform properties
    const elementsWithWillChange = document.querySelectorAll(
      '[style*="will-change"]',
    );
    const elementsWithTransform = document.querySelectorAll(
      '[style*="transform"]',
    );

    this.metrics.gpuLayersActive =
      elementsWithWillChange.length + elementsWithTransform.length;
  }

  private checkForIssues(): void {
    // Check for frame drops
    if (this.metrics.frameRate < 50) {
      this.metrics.frameDropCount++;
    }

    // Check for performance issues
    if (
      this.metrics.frameDropCount > 10 ||
      this.metrics.animationLatency > 50
    ) {
      this.onPerformanceIssue?.(this.metrics);
    }

    // Check memory usage
    if (this.metrics.memoryUsage > this.config.memoryThreshold) {
      this.onMemoryThresholdExceeded?.(this.metrics.memoryUsage);
    }
  }

  getMetrics(): AnimationPerformanceMetrics {
    return { ...this.metrics };
  }
}

/**
 * Reduced Motion Manager
 * Handles reduced motion preferences and fallbacks
 */
class ReducedMotionManager {
  private reducedMotionEnabled: boolean = false;
  private userPreference: boolean = false;
  private systemSuggestion: boolean = false;

  constructor() {
    this.checkSystemPreference();
    this.setupMediaQueryListener();
  }

  private checkSystemPreference(): void {
    if (window.matchMedia) {
      this.userPreference = window.matchMedia(
        "(prefers-reduced-motion: reduce)",
      ).matches;
      this.updateReducedMotionState();
    }
  }

  private setupMediaQueryListener(): void {
    if (window.matchMedia) {
      const mediaQuery = window.matchMedia("(prefers-reduced-motion: reduce)");
      mediaQuery.addEventListener("change", (e) => {
        this.userPreference = e.matches;
        this.updateReducedMotionState();
      });
    }
  }

  private updateReducedMotionState(): void {
    this.reducedMotionEnabled = this.userPreference || this.systemSuggestion;
  }

  shouldUseReducedMotion(): boolean {
    return this.reducedMotionEnabled;
  }

  suggestReducedMotion(): void {
    this.systemSuggestion = true;
    this.updateReducedMotionState();
  }

  getReducedMotionTiming(timing: AnimationTimingConfig): AnimationTimingConfig {
    return {
      ...timing,
      duration: Math.min(timing.duration, 100),
      iterations: 1,
      delay: Math.min(timing.delay, 50),
    };
  }

  getReducedMotionProperties(
    properties: GPUAnimationProperties,
  ): GPUAnimationProperties {
    return {
      ...properties,
      // Keep final state but remove intermediate transformations
      transform: properties.transform
        .replace(/scale\([^)]+\)/g, "scale(1)")
        .replace(/rotate\([^)]+\)/g, "rotate(0deg)"),
    };
  }
}

/**
 * Animation Memory Manager
 * Manages memory usage of animations
 */
class AnimationMemoryManager {
  private config: PerformanceConfig;
  private memoryUsageHistory: number[] = [];

  constructor(config: PerformanceConfig) {
    this.config = config;
  }

  triggerCleanup(): void {
    // Clean up unused animations
    this.cleanupUnusedAnimations();

    // Clean up DOM references
    this.cleanupDOMReferences();

    // Force garbage collection if available
    this.forceGarbageCollection();
  }

  private cleanupUnusedAnimations(): void {
    // Remove completed animations from DOM
    document
      .querySelectorAll('[data-animation-completed="true"]')
      .forEach((element) => {
        const animationId = element.getAttribute("data-animation-id");
        if (animationId) {
          element.removeAttribute("data-animation-id");
          element.removeAttribute("data-animation-completed");
        }
      });
  }

  private cleanupDOMReferences(): void {
    // Clean up will-change properties from completed animations
    document.querySelectorAll('[style*="will-change"]').forEach((element) => {
      const computedStyle = getComputedStyle(element as Element);
      const hasActiveAnimation =
        (element as HTMLElement).style.animation !== "";

      if (!hasActiveAnimation) {
        (element as HTMLElement).style.willChange = "auto";
      }
    });
  }

  private forceGarbageCollection(): void {
    // Force GC if available (development/testing environments)
    if ("gc" in window) {
      (window as any).gc();
    }

    // Create memory pressure to encourage GC
    const tempArray = new Array(1000000).fill(0);
    tempArray.length = 0;
  }

  dispose(): void {
    this.memoryUsageHistory = [];
  }
}

/**
 * Adaptive Quality System
 * Adjusts animation quality based on performance
 */
class AdaptiveQualitySystem {
  private config: PerformanceConfig;
  private currentQuality: "low" | "medium" | "high" = "high";

  public onQualityAdjustment?: (quality: "low" | "medium" | "high") => void;

  constructor(config: PerformanceConfig) {
    this.config = config;
  }

  adjustProperties(properties: GPUAnimationProperties): GPUAnimationProperties {
    switch (this.currentQuality) {
      case "low":
        return this.getLowQualityProperties(properties);
      case "medium":
        return this.getMediumQualityProperties(properties);
      case "high":
      default:
        return properties;
    }
  }

  adjustTiming(timing: AnimationTimingConfig): AnimationTimingConfig {
    switch (this.currentQuality) {
      case "low":
        return this.getLowQualityTiming(timing);
      case "medium":
        return this.getMediumQualityTiming(timing);
      case "high":
      default:
        return timing;
    }
  }

  reduceQuality(): void {
    if (this.currentQuality === "high") {
      this.currentQuality = "medium";
    } else if (this.currentQuality === "medium") {
      this.currentQuality = "low";
    }

    this.onQualityAdjustment?.(this.currentQuality);
  }

  increaseQuality(): void {
    if (this.currentQuality === "low") {
      this.currentQuality = "medium";
    } else if (this.currentQuality === "medium") {
      this.currentQuality = "high";
    }

    this.onQualityAdjustment?.(this.currentQuality);
  }

  private getLowQualityProperties(
    properties: GPUAnimationProperties,
  ): GPUAnimationProperties {
    return {
      ...properties,
      // Simplify transforms
      transform: properties.transform.replace(/scale\([^)]+\)/g, "scale(1)"),
    };
  }

  private getMediumQualityProperties(
    properties: GPUAnimationProperties,
  ): GPUAnimationProperties {
    return properties; // Keep most properties but may simplify complex ones
  }

  private getLowQualityTiming(
    timing: AnimationTimingConfig,
  ): AnimationTimingConfig {
    return {
      ...timing,
      duration: timing.duration * 0.5, // Faster animations
      easing: "linear", // Simpler easing
    };
  }

  private getMediumQualityTiming(
    timing: AnimationTimingConfig,
  ): AnimationTimingConfig {
    return {
      ...timing,
      duration: timing.duration * 0.75, // Slightly faster
    };
  }

  dispose(): void {
    // Cleanup if needed
  }
}

/**
 * Pooled Animation Class
 * Reusable animation instance for memory efficiency
 */
class PooledAnimation {
  private element: HTMLElement;
  private properties: GPUAnimationProperties;
  private timing: AnimationTimingConfig;
  private animation: Animation | null = null;

  constructor(
    element: HTMLElement,
    properties: GPUAnimationProperties,
    timing: AnimationTimingConfig,
  ) {
    this.element = element;
    this.properties = properties;
    this.timing = timing;
  }

  reset(): void {
    if (this.animation) {
      this.animation.cancel();
      this.animation = null;
    }
  }

  configure(
    element: HTMLElement,
    properties: GPUAnimationProperties,
    timing: AnimationTimingConfig,
  ): void {
    this.element = element;
    this.properties = properties;
    this.timing = timing;
  }

  async play(): Promise<void> {
    const keyframes = [
      { transform: "translateZ(0)", opacity: 1 },
      {
        transform: this.properties.transform,
        opacity: this.properties.opacity,
      },
    ];

    this.animation = this.element.animate(keyframes, this.timing);

    return new Promise<void>((resolve) => {
      if (this.animation) {
        this.animation.addEventListener("finish", () => resolve());
        this.animation.addEventListener("cancel", () => resolve());
      } else {
        resolve();
      }
    });
  }

  dispose(): void {
    this.reset();
  }
}

// Supporting interfaces
interface PerformanceConfig {
  enablePooling: boolean;
  enableBatching: boolean;
  enableAdaptiveQuality: boolean;
  enableMemoryManagement: boolean;
  maxConcurrentAnimations: number;
  targetFrameRate: number;
  memoryThreshold: number;
}

interface OptimizedAnimationOptions {
  id?: string;
  animationType?: string;
  priority?: "high" | "normal" | "low";
  usePool?: boolean;
  batchable?: boolean;
  callback?: () => void;
}
